// EXPORTS: WeightSuggestion, getWeightSuggestion, isLowerBodyExercise, getCycleWeekInfo, detectLayoff, getConsecutiveUnderperformCount
/**
 * 自适应渐进重量建议系统
 *
 * 核心原则：
 * 1. 第 1 次训练用户自填建立基准，系统不做任何估算
 * 2. 之后每次训练基于"完成次数 + 组感受"自动给出下次重量建议
 * 3. 永远只用用户真实记录，不估算、不测 1RM
 */

import type { BodyPart } from '@/data/exercises';
import type { IExerciseRecord, ISetRecord } from '@/hooks/useAppState';

export interface WeightSuggestion {
  /** 建议重量（kg），自重动作为 0 */
  suggestedWeight: number;
  /** 建议方向：up=加重 / maintain=维持 / down=减重 */
  direction: 'up' | 'maintain' | 'down';
  /** 变化量（kg，正数=加重，负数=减重，0=维持） */
  delta: number;
  /** 给用户看的简短文案 */
  reason: string;
  /** 详细说明（可选） */
  detail?: string;
}

// 目标次数区间：默认 8~12 次（动作库 reps 字段形如 "8-12"）
// 解析动作 reps 范围；解析失败则用 8~12 兜底
function parseRepsRange(repsStr: string): { low: number; high: number } {
  if (!repsStr) return { low: 8, high: 12 };
  const cleaned = repsStr.replace(/[^\d-]/g, '');
  if (cleaned.includes('-')) {
    const parts = cleaned.split('-').map(n => parseInt(n, 10));
    const low = parts[0] || 8;
    const high = parts[1] || 12;
    return { low: Math.min(low, high), high: Math.max(low, high) };
  }
  const n = parseInt(cleaned, 10);
  if (!isNaN(n) && n > 0) return { low: n, high: n };
  return { low: 8, high: 12 };
}

/** 判断是否为下肢动作（用于决定加重步幅：下肢 +5kg，上肢 +2.5kg） */
export function isLowerBodyExercise(bodyPart: BodyPart | string | undefined): boolean {
  if (!bodyPart) return false;
  return bodyPart === 'legs' || bodyPart === 'calves';
}

/** 步幅：上肢 2.5kg，下肢 5kg */
function stepDelta(bodyPart: BodyPart | string | undefined): number {
  return isLowerBodyExercise(bodyPart) ? 5 : 2.5;
}

/**
 * 动作感受汇总：优先读取动作级 feeling（新版，动作完成后一次性选择），
 * 若没有则降级从组感受取众数（兼容历史数据）。
 * 只统计 weight > 0 的正式组（自重/热身不计）
 */
function aggregateFeeling(record: IExerciseRecord): 'easy' | 'moderate' | 'hard' | undefined {
  // 新版：动作级 feeling 优先
  if (record.feeling) return record.feeling;
  // 降级：从各组感受取众数（兼容历史数据）
  const valid = record.sets.filter(s => s.completed && s.weight > 0);
  if (valid.length === 0) return undefined;
  const count: Record<string, number> = { easy: 0, moderate: 0, hard: 0 };
  for (const s of valid) {
    if (s.feeling) count[s.feeling] = (count[s.feeling] || 0) + 1;
  }
  let best: 'easy' | 'moderate' | 'hard' | undefined;
  let bestCount = 0;
  for (const k of ['easy', 'moderate', 'hard'] as const) {
    if (count[k] > bestCount) {
      best = k;
      bestCount = count[k];
    }
  }
  return bestCount > 0 ? best : undefined;
}

/**
 * 取有效组中「最高次数」（仅用于辅助展示，不再作为判定依据）
 * 只统计 weight > 0 且 completed 的正式组
 */
function getBestReps(sets: ISetRecord[]): number | null {
  const valid = sets.filter(s => s.completed && s.weight > 0);
  if (valid.length === 0) return null;
  return Math.max(...valid.map(s => s.reps));
}

/**
 * 取有效组中「最低次数」——作为是否全员达标的判定基准
 * 只统计 weight > 0 且 completed 的正式组
 */
function getMinReps(sets: ISetRecord[]): number | null {
  const valid = sets.filter(s => s.completed && s.weight > 0);
  if (valid.length === 0) return null;
  return Math.min(...valid.map(s => s.reps));
}

/** 全部有效组都达到目标次数下限 */
function allSetsReachLow(sets: ISetRecord[], low: number): boolean {
  const valid = sets.filter(s => s.completed && s.weight > 0);
  if (valid.length === 0) return false;
  return valid.every(s => s.reps >= low);
}

/** 全部有效组都达到或超过目标次数上限 */
function allSetsReachHigh(sets: ISetRecord[], high: number): boolean {
  const valid = sets.filter(s => s.completed && s.weight > 0);
  if (valid.length === 0) return false;
  return valid.every(s => s.reps >= high);
}

/** 取有效组的重量（所有完成正式组应重量一致；取第一个非零值） */
function getWorkingWeight(sets: ISetRecord[]): number {
  const valid = sets.filter(s => s.completed && s.weight > 0);
  return valid[0]?.weight ?? 0;
}

/**
 * 核心：基于上次训练记录 + 动作信息，计算下次重量建议
 *
 * 【全部组判定规则】
 * 1. 所有组都达到目标次数下限 且 整体感受为「轻松」→ 加 1 档（上肢+2.5kg / 下肢+5kg）
 * 2. 所有组都达到目标下限（全员达标），但感受为「适中」或「费力」 → 维持
 * 3. 只要有任意一组未达到目标下限 → 减 1 档，提示"有组未达标，先打好基础"
 * 4. 连续 2 次训练存在未达标组 → 减 1 档 + 加强警示
 * 首次训练（无历史）不给出建议
 */
export function getWeightSuggestion(
  lastRecord: IExerciseRecord | undefined,
  bodyPart: BodyPart | string | undefined,
  repsStr: string,
  isBodyweight: boolean | undefined,
): WeightSuggestion | null {
  // 第 1 次（无历史）不建议
  if (!lastRecord || !lastRecord.sets || lastRecord.sets.length === 0) return null;

  // 自重动作无重量概念
  if (isBodyweight) return null;

  const workingWeight = getWorkingWeight(lastRecord.sets);
  // 历史记录里重量全是 0 → 当首次处理
  if (workingWeight <= 0) return null;

  const step = stepDelta(bodyPart);
  const { low, high } = parseRepsRange(repsStr);
  const minReps = getMinReps(lastRecord.sets);
  const bestReps = getBestReps(lastRecord.sets);
  const feeling = aggregateFeeling(lastRecord);
  const validCount = lastRecord.sets.filter(s => s.completed && s.weight > 0).length;

  // 若没有有效次数（极端情况），维持
  if (minReps === null || validCount === 0) {
    return {
      suggestedWeight: workingWeight,
      direction: 'maintain',
      delta: 0,
      reason: '保持当前重量',
    };
  }

  // ========= 有任意一组未达目标下限 → 减重 =========
  if (!allSetsReachLow(lastRecord.sets, low)) {
    const newWeight = Math.max(step, workingWeight - step); // 不低于步幅
    return {
      suggestedWeight: newWeight,
      direction: 'down',
      delta: -(workingWeight - newWeight),
      reason: '有组未达标，先打好基础',
      detail: `${validCount} 组中最少 ${minReps} 次，低于目标 ${low} 次`,
    };
  }

  // ========= 全部组达到目标下限 + 感受轻松 → 加重 =========
  if (allSetsReachLow(lastRecord.sets, low) && feeling === 'easy') {
    return {
      suggestedWeight: workingWeight + step,
      direction: 'up',
      delta: step,
      reason: '全员达标且轻松，下次可加重',
      detail: `${validCount} 组全部 ≥ ${low} 次，感受轻松 → +${step}kg`,
    };
  }

  // ========= 全部组达标 + 感受费力 → 维持 =========
  if (feeling === 'hard') {
    return {
      suggestedWeight: workingWeight,
      direction: 'maintain',
      delta: 0,
      reason: '全员达标但偏费力，先稳住',
      detail: `最少 ${minReps} 次（目标≥${low}），感受费力，巩固后再加`,
    };
  }

  // ========= 全部组达标 + 感受适中 → 维持 =========
  if (feeling === 'moderate' || feeling === undefined) {
    return {
      suggestedWeight: workingWeight,
      direction: 'maintain',
      delta: 0,
      reason: '全员达标，继续巩固',
      detail: `最少 ${minReps} 次（目标≥${low}），感受适中`,
    };
  }

  // ========= 兜底：维持 =========
  return {
    suggestedWeight: workingWeight,
    direction: 'maintain',
    delta: 0,
    reason: '保持当前重量',
    detail: `上次最少 ${minReps} 次（目标≥${low}）`,
  };
}

// ========== 4 周周期强度循环 ==========

export interface CycleWeekInfo {
  /** 当前第几周（1~4） */
  weekOfCycle: number;
  /** 本周描述 */
  label: string;
  /** 本周组数 */
  sets: number;
  /** 本周次数目标 */
  reps: number;
  /** 重量系数（相对基准） */
  intensityFactor: number;
}

/**
 * 4 周周期强度循环
 * - 第 1 周：3×8，基准重量（1.0x），打底
 * - 第 2 周：4×6，基准+（1.05x），加量
 * - 第 3 周：4×4，基准++（1.1x），上重量
 * - 第 4 周：维持第 3 周重量、只减次数（减负周，不做减重）→ 1.1x 但 3×5
 *
 * startDate 为计划开始时间戳（ms）；基于自然日计算，不依赖打卡
 */
export function getCycleWeekInfo(startDate: number | undefined, todayMs?: number): CycleWeekInfo | null {
  if (!startDate) return null;
  const now = todayMs ?? Date.now();
  const dayMs = 86400000;
  const daysSinceStart = Math.floor((now - startDate) / dayMs);
  if (daysSinceStart < 0) return null;

  const weekOfCycle = (daysSinceStart % 28 < 7 ? 1
    : daysSinceStart % 28 < 14 ? 2
    : daysSinceStart % 28 < 21 ? 3 : 4);

  const configs: Record<number, Omit<CycleWeekInfo, 'weekOfCycle'>> = {
    1: { label: '第 1 周 · 打底', sets: 3, reps: 8, intensityFactor: 1.0 },
    2: { label: '第 2 周 · 加量', sets: 4, reps: 6, intensityFactor: 1.05 },
    3: { label: '第 3 周 · 上重量', sets: 4, reps: 4, intensityFactor: 1.1 },
    4: { label: '第 4 周 · 减负', sets: 3, reps: 5, intensityFactor: 1.1 },
  };

  return { weekOfCycle, ...configs[weekOfCycle] };
}

// ========== 停练检测 ==========

const LAYOFF_THRESHOLD_DAYS = 14;

/**
 * 检查是否停练超过阈值（14 天）
 * 返回 { isLayoff, daysSinceLast, suggestedFactor }
 * - 停练超 14 天：建议按上次 80% 恢复起步
 */
export function detectLayoff(
  lastRecordDate: string | undefined, // YYYY-MM-DD
  todayStr?: string,
): { isLayoff: boolean; daysSinceLast: number; suggestedFactor: number } {
  if (!lastRecordDate) return { isLayoff: false, daysSinceLast: 0, suggestedFactor: 1 };
  const today = todayStr ?? new Date().toISOString().slice(0, 10);
  const days = Math.floor((new Date(today).getTime() - new Date(lastRecordDate).getTime()) / 86400000);
  if (days > LAYOFF_THRESHOLD_DAYS) {
    return { isLayoff: true, daysSinceLast: days, suggestedFactor: 0.8 };
  }
  return { isLayoff: false, daysSinceLast: days, suggestedFactor: 1 };
}

// ========== 连续未达下限检测 ==========

/**
 * 统计最近 N 次训练中，某动作连续「存在未达标组」的次数
 * 判定标准：只要有任意一组次数 < 目标下限，就算该次训练未达标
 * 从最近一次开始往前数，遇到一次全员达标的就停止计数
 */
export function getConsecutiveUnderperformCount(
  records: Array<{ date: string; exercises: IExerciseRecord[] }>,
  exerciseId: string,
  repsStr: string,
): number {
  const { low } = parseRepsRange(repsStr);
  let consecutive = 0;
  // records 按日期倒序（最新在前）
  for (const rec of records) {
    const ex = rec.exercises.find(e => e.exerciseId === exerciseId);
    if (!ex) continue;
    const minReps = getMinReps(ex.sets);
    if (minReps === null) continue;
    if (!allSetsReachLow(ex.sets, low)) {
      consecutive++;
    } else {
      break; // 遇到全员达标，停止
    }
  }
  return consecutive;
}
